import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { FiUpload, FiX, FiCheckCircle, FiAlertCircle, FiLoader } from 'react-icons/fi';
import { CandidateServices } from '@/services/CandidateServices';
import { useNotification } from '@/contexts/NotificationContext';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: ${theme.spacing.lg};
`;

const Modal = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${theme.shadows.xl};
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const Header = styled.div`
  padding: ${theme.spacing.xl};
  border-bottom: 2px solid ${theme.colors.surface};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};

  svg {
    color: ${theme.colors.primary};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${theme.colors.text}60;
  cursor: pointer;
  padding: ${theme.spacing.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
    color: ${theme.colors.text};
  }
`;

const Content = styled.div`
  padding: ${theme.spacing.xl};
`;

const Description = styled.p`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text}80;
  margin: 0 0 ${theme.spacing.xl} 0;
  line-height: 1.6;
`;

const InfoBox = styled.div`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.info}10;
  border: 1px solid ${theme.colors.info};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.lg};
`;

const InfoText = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
  margin: 0;
  line-height: 1.6;
`;

const ResultSection = styled.div<{ $type: 'success' | 'error' }>`
  margin-top: ${theme.spacing.lg};
  padding: ${theme.spacing.lg};
  background: ${props => props.$type === 'success' ? theme.colors.success + '10' : theme.colors.error + '10'};
  border: 1px solid ${props => props.$type === 'success' ? theme.colors.success : theme.colors.error};
  border-radius: ${theme.borderRadius.md};
`;

const ResultTitle = styled.h4<{ $color: string }>`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  svg {
    color: ${props => props.$color};
  }
`;

const ResultDetails = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
`;

const FieldItem = styled.div`
  margin-bottom: ${theme.spacing.xs};
  display: flex;
  gap: ${theme.spacing.sm};

  strong {
    color: ${theme.colors.text};
    min-width: 200px;
  }
`;

const Footer = styled.div`
  padding: ${theme.spacing.xl};
  border-top: 2px solid ${theme.colors.surface};
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing.md};
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  border: none;

  ${props => props.$variant === 'primary' ? `
    background: ${theme.colors.primary};
    color: white;
    &:hover:not(:disabled) {
      background: ${theme.colors.primary}dd;
      transform: translateY(-2px);
      box-shadow: ${theme.shadows.md};
    }
  ` : `
    background: white;
    color: ${theme.colors.text};
    border: 2px solid ${theme.colors.border};
    &:hover:not(:disabled) {
      background: ${theme.colors.surface};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled(FiLoader)`
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const FileUploadSection = styled.div`
  margin-top: ${theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const FileUploadItem = styled.div`
  padding: ${theme.spacing.md};
  border: 2px dashed ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.surface};
  }

  label {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.sm};
    cursor: pointer;

    strong {
      font-size: ${theme.typography.sizes.sm};
      font-weight: ${theme.typography.weights.semibold};
      color: ${theme.colors.text};
    }

    input[type="file"] {
      display: none;
    }

    .upload-zone {
      padding: ${theme.spacing.md};
      border-radius: ${theme.borderRadius.sm};
      text-align: center;
      color: ${theme.colors.text}80;
      font-size: ${theme.typography.sizes.sm};

      &:hover {
        color: ${theme.colors.primary};
      }
    }

    .file-name {
      color: ${theme.colors.success};
      font-size: ${theme.typography.sizes.sm};
      display: flex;
      align-items: center;
      gap: ${theme.spacing.xs};
    }
  }
`;

interface AutoFillModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDataExtracted: (data: any) => void;
  candidateId: number | null;
  availableFiles?: {[key: string]: File}; // Fichiers déjà uploadés dans le formulaire
  onFilesUploaded?: (docType: string, file: File) => void; // Callback pour synchroniser les uploads
}

export const AutoFillModal: React.FC<AutoFillModalProps> = ({
  isOpen,
  onClose,
  onDataExtracted,
  candidateId,
  availableFiles = {},
  onFilesUploaded
}) => {
  const notification = useNotification();
  const [extracting, setExtracting] = useState(false);
  const [extractionResult, setExtractionResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleFileChange = (documentType: string, file: File | null) => {
    if (file && onFilesUploaded) {
      // Notifier le parent pour qu'il ajoute le fichier à son state
      onFilesUploaded(documentType, file);
    }
  };

  const getDocumentLabel = (docType: string): string => {
    const labels: {[key: string]: string} = {
      'id-card': 'Pièce d\'identité',
      'payslips': 'Bulletin de salaire',
      'payslips-1': 'Bulletin de salaire (dernier mois)',
      'payslips-2': 'Bulletin de salaire (avant-dernier mois)',
      'december-payslips': 'Bulletin de salaire (décembre)',
      'bank-statements': 'Relevé bancaire',
      'mobile-money-statements': 'Relevé Mobile Money',
      'kbis': 'Extrait KBIS'
    };
    return labels[docType] || docType;
  };

  const handleExtract = async () => {
    try {
      setExtracting(true);
      setError(null);
      setExtractionResult(null);

      let response;

      // MODE 1: Candidat existe déjà (utiliser les documents existants)
      if (candidateId) {
        response = await CandidateServices.autoFillFromDocuments(candidateId) as {
          success: boolean;
          data?: any;
          message?: string;
        };
      }
      // MODE 2: Utiliser les fichiers déjà uploadés dans le formulaire
      else {
        // Vérifier qu'au moins un fichier est disponible
        if (Object.keys(availableFiles).length === 0) {
          notification.toast.warning('Veuillez uploader au moins un document dans le formulaire avant d\'utiliser le remplissage automatique');
          setExtracting(false);
          return;
        }

        // Créer FormData avec les fichiers disponibles
        // Mapper les types de documents du formulaire vers les clés attendues par le backend
        const formData = new FormData();
        const documentTypeMapping: {[key: string]: string} = {
          'id-card': 'id_card',
          'payslips': 'payslip',
          'payslips-1': 'payslip',
          'payslips-2': 'payslip',
          'december-payslips': 'payslip',
          'bank-statements': 'bank_statement',
          'mobile-money-statements': 'mobile_money',
          'kbis': 'kbis'
        };

        let fileCount = 0;
        Object.entries(availableFiles).forEach(([docType, file]) => {
          const backendKey = documentTypeMapping[docType] || docType;
          // Éviter les doublons (si plusieurs payslips, ne garder que le premier)
          if (!formData.has(backendKey)) {
            formData.append(backendKey, file);
            fileCount++;
          }
        });

        if (fileCount === 0) {
          notification.toast.warning('Aucun document compatible trouvé pour l\'extraction');
          setExtracting(false);
          return;
        }

        response = await CandidateServices.autoFillFromUploadedDocuments(formData) as {
          success: boolean;
          data?: any;
          message?: string;
        };
      }

      if (response.success && response.data) {
        setExtractionResult(response.data);
        notification.toast.success(response.message || 'Extraction réussie !');

        // Appeler le callback pour remplir le formulaire
        onDataExtracted({
          ...response.data.personal_info,
          ...response.data.financial_info
        });
      } else {
        setError(response.message || 'Erreur lors de l\'extraction');
        notification.toast.error(response.message || 'Erreur lors de l\'extraction');
      }
    } catch (err: any) {
      const errorMsg = err.message || 'Une erreur est survenue lors de l\'extraction';
      setError(errorMsg);
      notification.toast.error(errorMsg);
    } finally {
      setExtracting(false);
    }
  };

  const handleClose = () => {
    setExtractionResult(null);
    setError(null);
    onClose();
  };

  return (
    <Overlay onClick={handleClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>
            <FiUpload />
            Remplissage automatique
          </Title>
          <CloseButton onClick={handleClose}>
            <FiX />
          </CloseButton>
        </Header>

        <Content>
          <Description>
            {candidateId ? (
              <>
                Le système va analyser les documents déjà uploadés et pré-remplir
                automatiquement le formulaire avec les informations extraites.
              </>
            ) : (
              <>
                Uploadez vos documents ici ou utilisez ceux déjà présents dans le formulaire.
                Le système les analysera et pré-remplira automatiquement le formulaire.
              </>
            )}
          </Description>

          {Object.keys(availableFiles).length > 0 && (
            <InfoBox>
              <InfoText>
                <strong>Documents déjà uploadés :</strong>
                {Object.entries(availableFiles).map(([docType, file]) => (
                  <div key={docType} style={{ marginTop: theme.spacing.xs }}>
                    ✓ <strong>{getDocumentLabel(docType)}:</strong> {file.name}
                  </div>
                ))}
              </InfoText>
            </InfoBox>
          )}

          {!candidateId && (
            <>
              <div style={{
                margin: `${theme.spacing.lg} 0`,
                fontSize: theme.typography.sizes.sm,
                fontWeight: theme.typography.weights.semibold,
                color: theme.colors.text
              }}>
                {Object.keys(availableFiles).length > 0 ? 'Ajouter d\'autres documents :' : 'Uploader vos documents :'}
              </div>

              <FileUploadSection>
                {['id-card', 'payslips', 'bank-statements', 'mobile-money-statements', 'kbis'].map(docType => (
                  <FileUploadItem key={docType}>
                    <label>
                      <strong>{getDocumentLabel(docType)}</strong>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(docType, e.target.files?.[0] || null)}
                      />
                      {availableFiles[docType] ? (
                        <div className="file-name">
                          <FiCheckCircle /> {availableFiles[docType].name}
                        </div>
                      ) : (
                        <div className="upload-zone">
                          <FiUpload style={{ marginRight: theme.spacing.xs }} />
                          Cliquez pour uploader ou glissez un fichier
                        </div>
                      )}
                    </label>
                  </FileUploadItem>
                ))}
              </FileUploadSection>

              <InfoBox style={{
                background: theme.colors.info + '10',
                border: `1px solid ${theme.colors.info}`,
                marginTop: theme.spacing.lg
              }}>
                <InfoText style={{ color: theme.colors.text }}>
                  💡 <strong>Astuce :</strong> Les documents uploadés ici seront automatiquement
                  ajoutés au formulaire principal. Pas besoin de les uploader deux fois !
                </InfoText>
              </InfoBox>
            </>
          )}

          {/* Résultats */}
          {extractionResult && (
            <ResultSection $type="success">
              <ResultTitle $color={theme.colors.success}>
                <FiCheckCircle />
                Extraction réussie !
              </ResultTitle>
              <ResultDetails>
                <p><strong>Informations extraites :</strong></p>
                {Object.keys(extractionResult.personal_info || {}).length > 0 && (
                  <>
                    <p style={{ marginTop: theme.spacing.md, marginBottom: theme.spacing.sm }}>
                      <strong>Informations personnelles :</strong>
                    </p>
                    {Object.entries(extractionResult.personal_info).map(([key, value]) => (
                      <FieldItem key={key}>
                        <strong>{key}:</strong>
                        <span>{String(value)}</span>
                      </FieldItem>
                    ))}
                  </>
                )}
                {Object.keys(extractionResult.financial_info || {}).length > 0 && (
                  <>
                    <p style={{ marginTop: theme.spacing.md, marginBottom: theme.spacing.sm }}>
                      <strong>Informations financières :</strong>
                    </p>
                    {Object.entries(extractionResult.financial_info).map(([key, value]) => (
                      <FieldItem key={key}>
                        <strong>{key}:</strong>
                        <span>{String(value)}</span>
                      </FieldItem>
                    ))}
                  </>
                )}
              </ResultDetails>
            </ResultSection>
          )}

          {/* Erreur */}
          {error && (
            <ResultSection $type="error">
              <ResultTitle $color={theme.colors.error}>
                <FiAlertCircle />
                Erreur lors de l'extraction
              </ResultTitle>
              <ResultDetails>{error}</ResultDetails>
            </ResultSection>
          )}
        </Content>

        <Footer>
          <Button onClick={handleClose}>
            {extractionResult ? 'Fermer' : 'Annuler'}
          </Button>
          <Button
            $variant="primary"
            onClick={handleExtract}
            disabled={extracting || Object.keys(availableFiles).length === 0}
          >
            {extracting ? (
              <>
                <LoadingSpinner />
                Extraction en cours...
              </>
            ) : (
              <>
                <FiUpload />
                Extraire et remplir
              </>
            )}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
};
